class HeroMarquee extends HTMLElement {
	constructor() {
		super();
		this.track = null;
		this.textTrack = null;
		this.pos = 0;
		this.speed = 60;
		this.baseSpeed = 60;
		this.contentHeight = 0;
		this.textContentHeight = 0;
		this.rafId = null;
		this.playing = true;
		this.isDragging = false;
		this.dragged = false;

		this.itemsMetrics = [];
		this.containerHeight = 0;

		this.bindEvents();
	}

	bindEvents() {
		this.tick = this.tick.bind(this);
		this.measure = this.measure.bind(this);
		this.onPointerDown = this.onPointerDown.bind(this);
		this.onPointerMove = this.onPointerMove.bind(this);
		this.endDrag = this.endDrag.bind(this);
	}

	connectedCallback() {
		this.container = this.querySelector('[data-marquee]');
		this.track = this.querySelector('[data-track]');
		this.textTrack = this.querySelector('[data-text-track]');

		if (!this.container || !this.track) return;

		// Disable native drag on images
		this.track.querySelectorAll('img, a').forEach((el) => {
			el.setAttribute('draggable', 'false');
			el.addEventListener('dragstart', (e) => e.preventDefault());
		});

		this.measure();
		this.addEventListeners();

		// Start animation
		this.lastTime = performance.now();
		this.rafId = requestAnimationFrame(this.tick);
	}

	disconnectedCallback() {
		if (this.rafId) cancelAnimationFrame(this.rafId);
		window.removeEventListener('resize', this.measure);
	}

	measure() {
		this.isMobile = window.matchMedia('(max-width: 992px)').matches;

		// Measure Main Track (Images)
		const items = Array.from(this.track.children);
		if (items.length === 0) return;

		const totalItems = items.length;
		const halfItems = Math.floor(totalItems / 2);

		const styles = getComputedStyle(this.track);
		const gap = parseFloat(styles.rowGap || styles.gap || '0') || 0;

		if (this.isMobile) {
			// Measure Widths for Mobile
			let w = 0;
			for (let i = 0; i < halfItems; i++) {
				w += items[i].offsetWidth;
				if (i < halfItems - 1) w += gap;
			}
			w += gap;
			this.contentSize = w;
			this.containerSize = this.container.offsetWidth;
		} else {
			// Measure Heights for Desktop
			let h = 0;
			for (let i = 0; i < halfItems; i++) {
				h += items[i].offsetHeight;
				if (i < halfItems - 1) h += gap;
			}
			h += gap;
			this.contentSize = h;
			this.containerSize = this.container.offsetHeight;
		}

		// Measure Text Track
		if (this.textTrack) {
			const textItems = Array.from(this.textTrack.children);
			if (textItems.length > 0) {
				const textHalfItems = Math.floor(textItems.length / 2);
				const textStyles = getComputedStyle(this.textTrack);
				const textGap = parseFloat(textStyles.rowGap || textStyles.gap || '0') || 0;

				let th = 0;
				if (this.isMobile) {
					for (let i = 0; i < textHalfItems; i++) {
						th += textItems[i].offsetWidth;
						if (i < textHalfItems - 1) th += textGap;
					}
					th += textGap;
				} else {
					for (let i = 0; i < textHalfItems; i++) {
						th += textItems[i].offsetHeight;
						if (i < textHalfItems - 1) th += textGap;
					}
					th += textGap;
				}
				this.textContentSize = th;
			}
		}
	}

	addEventListeners() {
		window.addEventListener('resize', this.measure);
		window.addEventListener('load', this.measure);

		// Pause on hover
		this.container.addEventListener('mouseenter', () => this.playing = false);
		this.container.addEventListener('mouseleave', () => {
			if (!this.isDragging) this.playing = true;
		});

		// Pointer Events for drag
		this.track.addEventListener('pointerdown', this.onPointerDown);
		window.addEventListener('pointermove', this.onPointerMove);
		window.addEventListener('pointerup', this.endDrag);
		window.addEventListener('pointercancel', this.endDrag);
	}

	wrapPosition(pos) {
		if (this.contentSize <= 0) return pos;
		if (pos <= -this.contentSize) pos += this.contentSize * Math.ceil((-pos) / this.contentSize);
		if (pos > 0) pos -= this.contentSize * Math.ceil(pos / this.contentSize);
		return pos;
	}

	tick(ts) {
		const dt = (ts - this.lastTime) / 1000;
		this.lastTime = ts;

		if (this.playing && !this.isDragging) {
			this.pos -= this.speed * dt;
			this.pos = this.wrapPosition(this.pos);

			if (this.isMobile) {
				this.track.style.transform = `translate3d(${this.pos}px, 0, 0)`;
			} else {
				this.track.style.transform = `translate3d(0, ${this.pos}px, 0)`;
			}

			if (this.textTrack && this.contentSize > 0) {
				const ratio = this.textContentSize / this.contentSize;
				let textPos = this.pos * ratio;

				if (this.isMobile) {
					this.textTrack.style.transform = `translate3d(${textPos}px, 0, 0)`;
				} else {
					this.textTrack.style.transform = `translate3d(0, ${textPos}px, 0)`;
				}
			}
		}

		// Manual drag updates are handled in onPointerMove

		this.rafId = requestAnimationFrame(this.tick);
	}

	onPointerDown(e) {
		this.isDragging = true;
		this.dragged = false;
		this.startCoord = this.isMobile ? e.clientX : e.clientY;
		this.startPos = this.pos;
		this.playing = false;
		this.track.setPointerCapture(e.pointerId);
		this.container.style.cursor = 'grabbing';
	}

	onPointerMove(e) {
		if (!this.isDragging) return;
		const currentCoord = this.isMobile ? e.clientX : e.clientY;
		const delta = currentCoord - this.startCoord;

		if (Math.abs(delta) > 5) this.dragged = true;
		this.pos = this.wrapPosition(this.startPos + delta);

		if (this.isMobile) {
			this.track.style.transform = `translate3d(${this.pos}px, 0, 0)`;
		} else {
			this.track.style.transform = `translate3d(0, ${this.pos}px, 0)`;
		}

		if (this.textTrack && this.contentSize > 0) {
			const ratio = this.textContentSize / this.contentSize;
			const textPos = this.pos * ratio;

			if (this.isMobile) {
				this.textTrack.style.transform = `translate3d(${textPos}px, 0, 0)`;
			} else {
				this.textTrack.style.transform = `translate3d(0, ${textPos}px, 0)`;
			}
		}
	}

	endDrag() {
		this.isDragging = false;
		this.container.style.cursor = 'grab';
		// On mobile (hover: none), we should always resume.
		// On desktop, we respect the :hover state (stay paused if still hovering).
		const hasHover = window.matchMedia('(hover: hover)').matches;
		if (!hasHover || !this.container.matches(':hover')) {
			this.playing = true;
		}
	}
}

customElements.define('hero-marquee', HeroMarquee);